import { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react'

type IconButtonVariant = 'ghost' | 'subtle' | 'filled'
type IconButtonSize = 'sm' | 'md' | 'lg'

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: IconButtonVariant
    size?: IconButtonSize
    icon: ReactNode
    color?: 'default' | 'primary' | 'danger'
}

const variantColorClasses: Record<IconButtonVariant, Record<string, string>> = {
    ghost: {
        default: 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-300 dark:hover:bg-slate-800',
        primary: 'text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-950',
        danger: 'text-red-500 hover:text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950',
    },
    subtle: {
        default:
            'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-300',
        primary: 'bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-400 dark:hover:bg-blue-900',
        danger: 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-950 dark:text-red-400 dark:hover:bg-red-900',
    },
    filled: {
        default: 'bg-slate-800 text-white hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600',
        primary: 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
        danger: 'bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600',
    },
}

const sizeClasses: Record<IconButtonSize, string> = {
    sm: 'h-5 w-5',
    md: 'h-7 w-7',
    lg: 'h-9 w-9',
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    ({ variant = 'ghost', size = 'sm', icon, color = 'default', className = '', ...props }, ref) => {
        return (
            <button
                ref={ref}
                className={`inline-flex cursor-pointer items-center justify-center rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variantColorClasses[variant][color]} ${sizeClasses[size]} ${className}`}
                {...props}
            >
                {icon}
            </button>
        )
    }
)

IconButton.displayName = 'IconButton'
